import { createFileRoute, useParams } from '@tanstack/react-router'
import { CenteredSpinner } from '@components/Loader/CenteredSpinner'
import { FailedToLoad } from '@components/Placeholders/FailedToLoad'
import { useGetProjectQuery } from '@features/projects/api'
import { ErrorBoundary } from '@sentry/react'
import { ManageProject } from '@features/projects/components/manage'
import {
  Card,
  CardBody,
  CardFooter,
  Button,
  Heading,
  Text,
  Skeleton,
} from '@chakra-ui/react'
import { withSentryErrorReporting } from '@components/Fallback'
import { Route as ManageProjectRoute } from '@routes/_auth/o/$orgId/projects/$projectId/manage/index'

const ManageProjectsFallback = ({ orgId }: { orgId: string }) => (
  <Card variant={'outline'}>
    <CardBody>
      <Heading size="md">
        There seems to be a problem loading this project.
      </Heading>
      <Text py="2">
        Try loading the page again or select another project from the list
      </Text>
    </CardBody>
    <CardFooter>
      <a href={`/o/${orgId}/projects`}>
        <Button>Back to project list</Button>
      </a>
    </CardFooter>
  </Card>
)

const ManagePage = () => {
  const { projectId, orgId } = useParams({
    from: ManageProjectRoute.id,
  })

  const {
    data: project,
    isLoading,
    isFetching,
    isError,
  } = useGetProjectQuery(projectId, {
    refetchOnMountOrArgChange: true,
  })

  return (
    <ErrorBoundary fallback={<ManageProjectsFallback orgId={orgId} />}>
      <Skeleton isLoaded={!isLoading} h={'full'} w={'full'}>
        {isError ? (
          <FailedToLoad
            message={'Unable to load project workflow'}
            h={'100%'}
            justifyContent={'center'}
            alignItems={'center'}
            display={'flex'}
          />
        ) : (
          <>
            {project && <ManageProject project={project} />}
            {isFetching && <CenteredSpinner />}
          </>
        )}
      </Skeleton>
    </ErrorBoundary>
  )
}

export const Route = createFileRoute(
  '/_auth/o/$orgId/projects/$projectId/manage/'
)({
  component: ManagePage,
  beforeLoad: () => ({ getTitle: () => 'Manage Project' }),
  errorComponent: withSentryErrorReporting(ManageProjectsFallback),
})
